import { useDebounce } from 'ahooks';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import DateRangePicker, { RangeType } from 'rsuite/DateRangePicker';
import 'rsuite/DateRangePicker/styles/index.css';
import { Agent } from 'src/interfaces/agent.interface';
import { CallFilters } from 'src/interfaces/call.interface';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';

/**
 * NOTE: Do not add a `disabled` prop to this component or its inputs.
 * Disabling inputs during loading causes them to lose focus, which creates
 * a poor UX when users are typing. Instead, loading state is shown in the
 * results table, and users can continue refining filters while results load.
 */
export type CallSelectorFiltersProps = {
  filters: CallFilters;
  onFiltersChange: (filters: CallFilters) => void;
  agents: Agent[];
  searchAgents: (name: string) => Promise<{ value: string; label: string }[]>;
};

export const CallSelectorFilters = ({
  filters,
  onFiltersChange,
  agents,
  searchAgents,
}: CallSelectorFiltersProps) => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
    if (
      filters.start_time?.comparison === 'btwn' &&
      Array.isArray(filters.start_time.value)
    ) {
      return [
        new Date(filters.start_time.value[0]),
        new Date(filters.start_time.value[1]),
      ];
    }
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    return [start, end];
  });

  // Local state for inputs to prevent focus loss during typing
  const [transcriptKeywordInput, setTranscriptKeywordInput] = useState(
    filters.transcript_keyword || '',
  );
  const [minDurationInput, setMinDurationInput] = useState(
    filters.duration?.value?.toString() ?? '',
  );

  // Debounced values (500ms delay)
  const debouncedTranscriptKeyword = useDebounce(transcriptKeywordInput, {
    wait: 500,
  });
  const debouncedMinDuration = useDebounce(minDurationInput, { wait: 500 });

  // Update filters when debounced transcript keyword changes (min 3 chars or empty)
  useEffect(() => {
    if (
      (debouncedTranscriptKeyword.length >= 3 ||
        debouncedTranscriptKeyword === '') &&
      debouncedTranscriptKeyword !== filters.transcript_keyword
    ) {
      onFiltersChange({
        ...filters,
        transcript_keyword: debouncedTranscriptKeyword,
      });
    }
  }, [debouncedTranscriptKeyword, filters, onFiltersChange]);

  // Update filters when debounced min duration changes
  useEffect(() => {
    const currentValue = filters.duration?.value;
    const trimmed = debouncedMinDuration.trim();

    if (trimmed === '') {
      if (currentValue !== undefined) {
        onFiltersChange({ ...filters, duration: undefined });
      }
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return; // Ignore invalid values
    }

    if (currentValue !== parsed) {
      onFiltersChange({
        ...filters,
        duration: { comparison: 'gte', value: parsed },
      });
    }
  }, [debouncedMinDuration, filters, onFiltersChange]);

  const agentsOptions = [
    { value: 'All Agents' },
    ...agents.map((agent: Agent) => ({
      value: agent.id as string,
      label: agent.name || getFirstAndLastFourUUID(agent.id as string),
    })),
  ];

  const customRanges = [
    {
      label: 'Last 28 days',
      value: [startOfDay(subDays(new Date(), 27)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Last 7 days',
      value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Yesterday',
      value: [
        startOfDay(subDays(new Date(), 1)),
        endOfDay(subDays(new Date(), 1)),
      ],
      closeOverlay: true,
    },
    {
      label: 'Today',
      value: [startOfDay(new Date()), endOfDay(new Date())],
      closeOverlay: true,
    },
  ];

  const updateFilters = useCallback(
    (newFilters: Partial<CallFilters>) => {
      onFiltersChange({ ...filters, ...newFilters });
    },
    [filters, onFiltersChange],
  );

  const handleDateRangeChange = useCallback(
    (value: [Date, Date] | null) => {
      if (!value) {
        setDateRange([null, null]);
        updateFilters({ start_time: null });
        return;
      }
      setDateRange(value);
      updateFilters({
        start_time: {
          comparison: 'btwn',
          value: [value[0].toISOString(), value[1].toISOString()],
        },
      });
    },
    [updateFilters],
  );

  return (
    <div className="p-4 border-b border-neutral-200 space-y-3 bg-neutral-50 flex-shrink-0">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-neutral-500 mb-1.5 block">
            Call ID
          </Label>
          <Input
            placeholder="Search by ID..."
            value={filters.call_id || ''}
            onChange={(e) => updateFilters({ call_id: e.target.value })}
            size="small"
          />
        </div>
        <div>
          <Label className="text-xs text-neutral-500 mb-1.5 block">Agent</Label>
          <AutoCompleteSelectBox
            key={`agent-${filters.agent}`}
            defaultValue={agentsOptions.find(
              (agent) => agent.value === filters.agent,
            )}
            fetchOptions={searchAgents}
            onChange={(value) => updateFilters({ agent: value })}
            variant="outlined"
            color="primary"
            size="small"
            defaultOptions={agentsOptions}
            allowRemoveOption={false}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-neutral-500 mb-1.5 block">
            Minimum Call Duration (seconds)
          </Label>
          <Input
            type="number"
            placeholder="e.g. 30"
            value={minDurationInput}
            onChange={(e) => setMinDurationInput(e.target.value)}
            size="small"
            min={0}
          />
        </div>
        <div>
          <Label className="text-xs text-neutral-500 mb-1.5 block">
            Search Transcript (at least 3 characters)
          </Label>
          <Input
            placeholder="Search in transcript..."
            value={transcriptKeywordInput}
            onChange={(e) => setTranscriptKeywordInput(e.target.value)}
            size="small"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs text-neutral-500 mb-1.5 block">
          Date Range
        </Label>
        <DateRangePicker
          format="MM/dd/yyyy"
          className="w-full call-selector-date-picker"
          value={dateRange as [Date, Date]}
          onChange={handleDateRangeChange}
          ranges={customRanges as RangeType[]}
          size="sm"
        />
      </div>
    </div>
  );
};
